/**
 * manager-drawer.js
 * Gerencia todos os drawers da página de forma declarativa.
 * Disponível como window.DrawerManager.
 *
 * ─── HTML esperado ────────────────────────────────────────────────────────
 *
 *   <!-- Drawer (section liquid no Theme Editor) -->
 *   <div data-drawer="[name]"
 *        data-drawer-side="left|right"
 *        [data-drawer-no-history]        ← desativa botão voltar no mobile
 *        aria-hidden="true">
 *
 *     <div data-drawer-overlay></div>    ← overlay, fecha ao clicar
 *
 *     <button data-drawer-close="[name]">Fechar</button>
 *
 *     <!-- conteúdo gerenciado pela section -->
 *   </div>
 *
 *   <!-- Triggers (em qualquer lugar do DOM) -->
 *   <button data-drawer-open="[name]">Abrir</button>
 *   <button data-drawer-close="[name]">Fechar</button>
 *   <button data-drawer-toggle="[name]">Alternar</button>
 *
 * ─── API pública ──────────────────────────────────────────────────────────
 *
 *   window.DrawerManager.open('cart')
 *   window.DrawerManager.close('cart')
 *   window.DrawerManager.toggle('menu')
 *   window.DrawerManager.closeAll()
 *   window.DrawerManager.isOpen('cart')  → boolean
 *   window.DrawerManager.on('drawer:open', ({ name, element }) => {})
 *   window.DrawerManager.on('drawer:close', ({ name, element }) => {})
 *   window.DrawerManager.destroy()
 *
 * ─── Eventos emitidos via window.eventBus ─────────────────────────────────
 *
 *   'drawer:open'  → { name, element }   ao iniciar abertura
 *   'drawer:close' → { name, element }   ao iniciar fechamento
 *
 * ─── Comportamentos ───────────────────────────────────────────────────────
 *
 *   - Abrir um drawer fecha todos os outros automaticamente
 *   - ESC fecha o drawer ativo
 *   - Overlay click fecha o drawer pai
 *   - Scroll lock no body quando qualquer drawer está aberto
 *   - Focus trap: Tab/Shift+Tab cicla dentro do drawer
 *   - Restaura foco ao elemento que abriu o drawer ao fechar
 *   - history.pushState em viewport < 990px (desativável por data-drawer-no-history)
 *   - Re-registra drawers ao shopify:section:load (Theme Editor)
 */

class DrawerManager {
  constructor() {
    /** @type {Map<string, { element: HTMLElement, historyStatePushed: boolean, opener: HTMLElement|null }>} */
    this._drawers = new Map();
    this._activeDrawer = null;
    this._MOBILE_BREAKPOINT = 990;

    this._onKeyDown    = this._onKeyDown.bind(this);
    this._onPopState   = this._onPopState.bind(this);
    this._onClick      = this._onClick.bind(this);
    this._onFocusIn    = this._onFocusIn.bind(this);

    this._init();
    this._bindGlobalEvents();
    this._observeThemeEditor();
  }

  // ─── Inicialização ────────────────────────────────────────────────────────

  _init() {
    document.querySelectorAll('[data-drawer]').forEach(el => this._register(el));
  }

  _register(el) {
    const name = el.dataset.drawer;
    if (!name || this._drawers.has(name)) return;
    el.setAttribute('aria-hidden', 'true');
    el.setAttribute('inert', '');
    this._drawers.set(name, { element: el, historyStatePushed: false, opener: null });
  }

  _observeThemeEditor() {
    document.addEventListener('shopify:section:load', () => {
      document.querySelectorAll('[data-drawer]').forEach(el => this._register(el));
    });
  }

  // ─── Eventos globais ──────────────────────────────────────────────────────

  _bindGlobalEvents() {
    document.addEventListener('keydown', this._onKeyDown);
    document.addEventListener('click',   this._onClick);
    document.addEventListener('focusin', this._onFocusIn);
    window.addEventListener('popstate',  this._onPopState);
  }

  _onClick(e) {
    const openBtn = e.target.closest('[data-drawer-open]');
    if (openBtn) {
      e.preventDefault();
      this.open(openBtn.dataset.drawerOpen, openBtn);
      return;
    }

    const closeBtn = e.target.closest('[data-drawer-close]');
    if (closeBtn) {
      e.preventDefault();
      this.close(closeBtn.dataset.drawerClose);
      return;
    }

    const toggleBtn = e.target.closest('[data-drawer-toggle]');
    if (toggleBtn) {
      e.preventDefault();
      this.toggle(toggleBtn.dataset.drawerToggle, toggleBtn);
      return;
    }

    const overlay = e.target.closest('[data-drawer-overlay]');
    if (overlay) {
      const drawerEl = overlay.closest('[data-drawer]');
      if (drawerEl) this.close(drawerEl.dataset.drawer);
      return;
    }
  }

  _onKeyDown(e) {
    if (!this._activeDrawer) return;

    if (e.key === 'Escape') {
      this.close(this._activeDrawer);
      return;
    }

    // Focus trap via Tab/Shift+Tab
    if (e.key === 'Tab') {
      this._trapFocus(e);
    }
  }

  _onFocusIn(e) {
    if (!this._activeDrawer) return;
    const drawer = this._drawers.get(this._activeDrawer);
    if (!drawer) return;
    // Se o foco saiu do drawer, devolve para dentro
    if (!drawer.element.contains(e.target)) {
      this._focusFirst(drawer.element);
    }
  }

  _onPopState() {
    if (!this._activeDrawer) return;
    const drawer = this._drawers.get(this._activeDrawer);
    if (!drawer) return;
    if (drawer.historyStatePushed) {
      drawer.historyStatePushed = false;
      this._closeDrawer(this._activeDrawer);
    }
  }

  // ─── API Pública ──────────────────────────────────────────────────────────

  /**
   * Abre um drawer pelo nome. Fecha o drawer ativo (se diferente) antes.
   * @param {string} name
   * @param {HTMLElement|null} opener - Elemento que disparou a abertura (para restaurar foco)
   */
  open(name, opener = null) {
    if (!this._drawers.has(name)) return;
    if (this._activeDrawer && this._activeDrawer !== name) {
      this._closeDrawer(this._activeDrawer, { silent: true });
    }
    this._openDrawer(name, opener);
  }

  /**
   * Fecha um drawer pelo nome (ou o ativo se omitido).
   * @param {string} [name]
   */
  close(name) {
    const target = name || this._activeDrawer;
    if (!target || !this._drawers.has(target)) return;
    const drawer = this._drawers.get(target);
    if (drawer.historyStatePushed) {
      history.back(); // dispara popstate → _closeDrawer
    } else {
      this._closeDrawer(target);
    }
  }

  /**
   * Alterna o estado de um drawer.
   * @param {string} name
   * @param {HTMLElement|null} opener
   */
  toggle(name, opener = null) {
    this.isOpen(name) ? this.close(name) : this.open(name, opener);
  }

  /** Fecha todos os drawers abertos. */
  closeAll() {
    if (this._activeDrawer) this.close(this._activeDrawer);
  }

  /**
   * @param {string} name
   * @returns {boolean}
   */
  isOpen(name) {
    const drawer = this._drawers.get(name);
    return drawer ? drawer.element.classList.contains('is-open') : false;
  }

  /**
   * Escuta eventos via eventBus.
   * @param {string} event - 'drawer:open' | 'drawer:close'
   * @param {Function} callback
   * @returns {Function} Função de cleanup
   */
  on(event, callback) {
    return window.eventBus?.on(event, callback);
  }

  /** Remove todos os event listeners e limpa o estado interno. */
  destroy() {
    document.removeEventListener('keydown', this._onKeyDown);
    document.removeEventListener('click',   this._onClick);
    document.removeEventListener('focusin', this._onFocusIn);
    window.removeEventListener('popstate',  this._onPopState);
    this._drawers.clear();
    this._activeDrawer = null;
  }

  // ─── Lógica Interna ───────────────────────────────────────────────────────

  _openDrawer(name, opener) {
    const drawer = this._drawers.get(name);
    if (!drawer) return;

    drawer.opener = opener instanceof HTMLElement ? opener : null;
    drawer.element.classList.add('is-open');
    drawer.element.removeAttribute('aria-hidden');
    drawer.element.removeAttribute('inert');
    document.body.classList.add('drawer-is-open');
    this._activeDrawer = name;

    // history.pushState apenas em mobile/tablet sem data-drawer-no-history
    if (
      !drawer.element.hasAttribute('data-drawer-no-history') &&
      window.innerWidth < this._MOBILE_BREAKPOINT
    ) {
      history.pushState({ drawerOpen: name }, '');
      drawer.historyStatePushed = true;
    }

    this._focusFirst(drawer.element);
    window.eventBus?.emit('drawer:open', { name, element: drawer.element });
  }

  _closeDrawer(name, options = {}) {
    const drawer = this._drawers.get(name);
    if (!drawer) return;

    drawer.element.classList.remove('is-open');
    drawer.element.setAttribute('aria-hidden', 'true');
    drawer.element.setAttribute('inert', '');
    drawer.historyStatePushed = false;
    this._activeDrawer = null;
    document.body.classList.remove('drawer-is-open');

    // Restaura foco ao elemento que abriu
    if (drawer.opener) {
      drawer.opener.focus();
      drawer.opener = null;
    }

    if (!options.silent) {
      window.eventBus?.emit('drawer:close', { name, element: drawer.element });
    }
  }

  /** Foca no primeiro elemento focável dentro do drawer. */
  _focusFirst(el) {
    const focusable = el.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length) {
      requestAnimationFrame(() => focusable[0].focus());
    }
  }

  /** Impede foco de sair do drawer com Tab/Shift+Tab. */
  _trapFocus(e) {
    const drawer = this._drawers.get(this._activeDrawer);
    if (!drawer) return;

    const focusable = [...drawer.element.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )];
    if (!focusable.length) return;

    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

window.DrawerManager = new DrawerManager();
